import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { PlusIcon, TrashIcon, CalendarIcon, MailIcon, SmartphoneIcon, CalendarDaysIcon, UsersIcon, BuildingIcon, PercentIcon, DollarSignIcon, AlertCircleIcon, InfoIcon, ChevronDownIcon, ChevronUpIcon, UserIcon } from 'lucide-react';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
import { SearchableSelect } from '../../../shared/ui/SearchableSelect';
import { mockSuppliers } from '../../suppliers/mocks/mockSuppliers';
export interface Recipient {
  id: number;
  email: string;
  phone: string;
}
export interface Supplier {
  id: number;
  name: string;
  email: string;
  amount: string;
  percentage?: string;
}
export interface Installment {
  id: number;
  amount: string;
  dueDate: string;
  sendLinkDate: string;
  description: string;
  recipient?: Recipient;
}
interface InstallmentPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onSplitTypeChange?: (type: 'dates' | 'recipients' | 'commission') => void;
  onInstallmentsChange?: (installments: Installment[]) => void;
  onSuppliersChange?: (suppliers: Supplier[]) => void;
  onBeneficiaryTypeChange?: (type: 'percentage' | 'fixed') => void;
  onTotalPaymentAmountChange?: (amount: string) => void;
}
export function InstallmentPayment({
  onExpiryDateChange,
  onSplitTypeChange,
  onInstallmentsChange,
  onSuppliersChange,
  onBeneficiaryTypeChange,
  onTotalPaymentAmountChange
}: InstallmentPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [installments, setInstallments] = useState<Installment[]>([{
    id: 1,
    amount: '',
    dueDate: '',
    sendLinkDate: '',
    description: '',
    recipient: {
      id: 1,
      email: '',
      phone: ''
    }
  }, {
    id: 2,
    amount: '',
    dueDate: '',
    sendLinkDate: '',
    description: '',
    recipient: {
      id: 2,
      email: '',
      phone: ''
    }
  }]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([{
    id: 1,
    name: '',
    email: '',
    amount: '',
    percentage: '0'
  }]);
  const [splitType, setSplitType] = useState<'dates' | 'recipients' | 'commission'>('dates');
  const [beneficiaryType, setBeneficiaryType] = useState<'percentage' | 'fixed'>('percentage');
  const [feeType, setFeeType] = useState<'supplier' | 'merchant' | 'split' | 'customer'>('supplier');
  const [passFeesToCustomer, setPassFeesToCustomer] = useState(false);
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [totalPaymentAmount, setTotalPaymentAmount] = useState('');
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [supplierCommissionTotal, setSupplierCommissionTotal] = useState(0);
  const [showDatePickers, setShowDatePickers] = useState<{
    [key: number]: {
      due: boolean;
      send: boolean;
    };
  }>({});
  // This would come from settings in a real app
  const defaultExpiryDays = 30;
  // Validate commission amounts don't exceed total payment
  useEffect(() => {
    if (splitType === 'commission' && totalPaymentAmount) {
      const totalAmount = parseFloat(totalPaymentAmount) || 0;
      // Calculate total commission amount
      const commissionTotal = suppliers.reduce((total, supplier) => {
        if (beneficiaryType === 'percentage') {
          // For percentage, we'll check in another validation
          return total;
        } else {
          // For fixed amount
          return total + (parseFloat(supplier.amount) || 0);
        }
      }, 0);
      setSupplierCommissionTotal(commissionTotal);
      if (beneficiaryType === 'fixed' && commissionTotal > totalAmount) {
        setCommissionError('Total commission amount cannot exceed the total payment amount');
      } else if (beneficiaryType === 'percentage') {
        const percentageTotal = suppliers.reduce((total, supplier) => {
          return total + (parseFloat(supplier.percentage || '0') || 0);
        }, 0);
        if (percentageTotal > 100) {
          setCommissionError('Total commission percentage cannot exceed 100%');
        } else {
          setCommissionError(null);
        }
      } else {
        setCommissionError(null);
      }
    }
  }, [splitType, totalPaymentAmount, suppliers, beneficiaryType]);
  // Report state changes to parent
  useEffect(() => {
    onSplitTypeChange?.(splitType);
  }, [splitType]);
  useEffect(() => {
    onInstallmentsChange?.(installments);
  }, [installments]);
  useEffect(() => {
    onSuppliersChange?.(suppliers);
  }, [suppliers]);
  useEffect(() => {
    onBeneficiaryTypeChange?.(beneficiaryType);
  }, [beneficiaryType]);
  useEffect(() => {
    onTotalPaymentAmountChange?.(totalPaymentAmount);
  }, [totalPaymentAmount]);
  // Calculate send link date when due date changes
  const calculateSendLinkDate = (dueDateStr: string): string => {
    if (!dueDateStr) return '';
    try {
      // Parse the due date
      const [month, day, year] = dueDateStr.split('/').map(part => parseInt(part));
      const dueDate = new Date(year, month - 1, day);
      // Subtract the default expiry days
      const sendDate = new Date(dueDate);
      sendDate.setDate(sendDate.getDate() - defaultExpiryDays);
      // Format the date as MM/DD/YYYY
      const sendMonth = (sendDate.getMonth() + 1).toString().padStart(2, '0');
      const sendDay = sendDate.getDate().toString().padStart(2, '0');
      const sendYear = sendDate.getFullYear();
      return `${sendMonth}/${sendDay}/${sendYear}`;
    } catch (error) {
      // If there's any error parsing, return empty string
      return '';
    }
  };
  const addInstallment = () => {
    const newId = Math.max(...installments.map(i => i.id), 0) + 1;
    setInstallments([...installments, {
      id: newId,
      amount: '',
      dueDate: '',
      sendLinkDate: '',
      description: '',
      recipient: {
        id: newId,
        email: '',
        phone: ''
      }
    }]);
  };
  const addSupplier = () => {
    const newId = Math.max(...suppliers.map(s => s.id), 0) + 1;
    setSuppliers([...suppliers, {
      id: newId,
      name: '',
      email: '',
      amount: '',
      percentage: '0'
    }]);
  };
  const removeInstallment = (id: number) => {
    if (installments.length > 1) {
      setInstallments(installments.filter(i => i.id !== id));
    }
  };
  const removeSupplier = (id: number) => {
    if (suppliers.length > 1) {
      setSuppliers(suppliers.filter(s => s.id !== id));
    }
  };
  const updateInstallment = (id: number, field: string, value: string) => {
    setInstallments(installments.map(installment => {
      if (installment.id === id) {
        const updatedInstallment = {
          ...installment,
          [field]: value
        };
        // If we're updating the due date and it's a valid date, update the send link date
        if (field === 'dueDate' && value) {
          updatedInstallment.sendLinkDate = calculateSendLinkDate(value);
        }
        return updatedInstallment;
      }
      return installment;
    }));
  };
  const updateSupplier = (id: number, field: string, value: string) => {
    setSuppliers(suppliers.map(supplier => supplier.id === id ? {
      ...supplier,
      [field]: value
    } : supplier));
  };
  const updateRecipient = (installmentId: number, field: 'email' | 'phone', value: string) => {
    setInstallments(installments.map(installment => installment.id === installmentId && installment.recipient ? {
      ...installment,
      recipient: {
        ...installment.recipient,
        [field]: value
      }
    } : installment));
  };
  const splitOptions = [{
    value: 'dates',
    label: 'By payment schedule',
    icon: <CalendarDaysIcon size={16} data-id="element-8436" />
  }, {
    value: 'recipients',
    label: 'By multiple people',
    icon: <UsersIcon size={16} data-id="element-8437" />
  }, {
    value: 'commission',
    label: 'By commission',
    icon: <PercentIcon size={16} data-id="element-8438" />
  }];
  const beneficiaryOptions = [{
    value: 'percentage',
    label: 'Percentage',
    icon: <PercentIcon size={16} data-id="element-8439" />
  }, {
    value: 'fixed',
    label: 'Fixed amount',
    icon: <DollarSignIcon size={16} data-id="element-8440" />
  }];
  const totalAmount = installments.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const supplierOptions = mockSuppliers.map(s => s.name);
  // Mock suppliers list for dropdown
  const suppliersList = [{
    id: 1,
    name: 'ABC Suppliers',
    email: 'abc@example.com'
  }, {
    id: 2,
    name: 'XYZ Merchants',
    email: 'xyz@example.com'
  }, {
    id: 3,
    name: '123 Distributors',
    email: '123@example.com'
  }, {
    id: 4,
    name: 'Global Partners',
    email: 'global@example.com'
  }];
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  const handleTotalPaymentAmountChange = (value: string) => {
    setTotalPaymentAmount(value);
  };
  const getCommissionAmountDisplay = () => {
    if (beneficiaryType === 'fixed') {
      return `${formatCurrencyAmount(supplierCommissionTotal, {
        fractionDigits: 2
      })} of ${formatCurrencyAmount(parseFloat(totalPaymentAmount || '0'), {
        fractionDigits: 2
      })}`;
    } else {
      const percentageTotal = suppliers.reduce((total, supplier) => {
        return total + (parseFloat(supplier.percentage || '0') || 0);
      }, 0);
      return `${percentageTotal.toFixed(2)}% of payment`;
    }
  };
  // Date picker helpers
  const toggleDatePicker = (id: number, type: 'due' | 'send') => {
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: prev[id] ? !prev[id][type] : true
      }
    }));
  };
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const handleDateSelect = (id: number, field: 'dueDate' | 'sendLinkDate', date: Date) => {
    // Update in a single state call to avoid stale state overwrites
    setInstallments(installments.map(installment => {
      if (installment.id === id) {
        const updated = {
          ...installment,
          [field]: formatDate(date)
        };
        // If selecting due date, also auto-calculate send link date
        if (field === 'dueDate') {
          const sendDate = new Date(date);
          sendDate.setDate(sendDate.getDate() - defaultExpiryDays);
          updated.sendLinkDate = formatDate(sendDate);
        }
        return updated;
      }
      return installment;
    }));
    // Close the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field === 'dueDate' ? 'due' : 'send']: false
      }
    }));
  };
  const generateCalendar = (dateStr: string) => {
    const today = new Date();
    const selectedDate = dateStr ? parseDate(dateStr) : today;
    const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysInMonth = lastDay.getDate();
    const weeks = [];
    let days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), i);
      days.push(date);
      if (days.length === 7 || i === daysInMonth) {
        // Fill remaining days of the week
        while (days.length < 7) {
          days.push(null);
        }
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: selectedDate,
      weeks
    };
  };
  const parseDate = (dateString: string): Date => {
    if (!dateString) return new Date();
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date, selectedDateStr: string): boolean => {
    if (!selectedDateStr) return false;
    const selectedDate = parseDate(selectedDateStr);
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const handlePrevMonth = (calendar: any, id: number, type: 'due' | 'send') => {
    const date = new Date(calendar.month);
    date.setMonth(date.getMonth() - 1);
    // Force re-render by toggling the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: false
      }
    }));
    setTimeout(() => {
      setShowDatePickers(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [type]: true
        }
      }));
    }, 10);
  };
  const handleNextMonth = (calendar: any, id: number, type: 'due' | 'send') => {
    const date = new Date(calendar.month);
    date.setMonth(date.getMonth() + 1);
    // Force re-render by toggling the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: false
      }
    }));
    setTimeout(() => {
      setShowDatePickers(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [type]: true
        }
      }));
    }, 10);
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] transition-colors placeholder:text-gray-400';
  const labelClass = 'block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2';
  return <div className="space-y-5" data-id="element-8441">
      {/* ─── SPLIT PAYMENT CONFIGURATION ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8442">
        <div className="p-6 space-y-6" data-id="element-8443">
          <div data-id="element-8444">
            <label className={labelClass} data-id="element-8445">
              {t('components.InstallmentPayment.how_would_you_like_to_split')}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-8446">
              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${splitType === 'dates' ? 'border-[var(--brand-primary)] bg-[var(--brand-primarysubtle)] ring-1 ring-[var(--brand-primary)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-8447">
                <input type="radio" name="splitType" className="mt-0.5 h-4 w-4 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] border-gray-300" checked={splitType === 'dates'} onChange={() => setSplitType('dates')} data-id="element-8448" />

                <div data-id="element-8449">
                  <div className="flex items-center gap-2 mb-1" data-id="element-8450">
                    <CalendarDaysIcon size={16} className={splitType === 'dates' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-8451" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-8452">
                      {t('components.InstallmentPayment.payment_schedule')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-8453">
                    {t('components.InstallmentPayment.split_into_multiple_payments_on_different')}
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${splitType === 'recipients' ? 'border-[var(--brand-primary)] bg-[var(--brand-primarysubtle)] ring-1 ring-[var(--brand-primary)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-8454">
                <input type="radio" name="splitType" className="mt-0.5 h-4 w-4 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] border-gray-300" checked={splitType === 'recipients'} onChange={() => setSplitType('recipients')} data-id="element-8455" />

                <div data-id="element-8456">
                  <div className="flex items-center gap-2 mb-1" data-id="element-8457">
                    <UsersIcon size={16} className={splitType === 'recipients' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-8458" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-8459">
                      {t('components.InstallmentPayment.multiple_people')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-8460">
                    {t('components.InstallmentPayment.split_the_payment_between_different_recipients')}
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${splitType === 'commission' ? 'border-[var(--brand-primary)] bg-[var(--brand-primarysubtle)] ring-1 ring-[var(--brand-primary)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-8461">
                <input type="radio" name="splitType" className="mt-0.5 h-4 w-4 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] border-gray-300" checked={splitType === 'commission'} onChange={() => setSplitType('commission')} data-id="element-8462" />

                <div data-id="element-8463">
                  <div className="flex items-center gap-2 mb-1" data-id="element-8464">
                    <PercentIcon size={16} className={splitType === 'commission' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-8465" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-8466">
                      {t('components.InstallmentPayment.commission_split')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-8467">
                    {t('components.InstallmentPayment.distribute_payment_between_your_business_and')}
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div data-id="element-8468">
            <label className={labelClass} data-id="element-8469">
              {t('components.InstallmentPayment.order_reference')}
            </label>
            <input type="text" className={inputClass} placeholder={t('components.InstallmentPayment.attrs.eg_order_1234_or_project_name')} data-id="element-8470" />
          </div>
        </div>
      </div>

      {/* ─── SPLIT DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-8471">
        <div className="px-6 py-4 flex items-start justify-between border-b border-gray-100" data-id="element-8472">
          <div data-id="element-8473">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-8474">
              {splitType === 'dates' ? 'Payment schedule' : splitType === 'recipients' ? 'Payment recipients' : 'Commission details'}
            </h3>
            {splitType === 'dates' && <p className="text-xs text-gray-500 mt-0.5 max-w-2xl" data-id="element-8475">
                {t('components.InstallmentPayment.payment_links_will_be_automatically_sent')}{' '}
                {defaultExpiryDays}{' '}
                {t('components.InstallmentPayment.days_before_the_due_date')}
              </p>}
            {splitType === 'commission' && <p className="text-xs text-gray-500 mt-0.5" data-id="element-8476">
                {t('components.InstallmentPayment.configure_supplier_commissions_and_how_payments')}
              </p>}
          </div>
          <div className="text-sm font-medium text-gray-500 flex-shrink-0" data-id="element-8477">
            {splitType === 'commission' ? `Commission: ${getCommissionAmountDisplay()}` : `Total: ${formatCurrencyAmount(totalAmount, {
            fractionDigits: 2
          })}`}
          </div>
        </div>
        <div className="p-6" data-id="element-8478">
          {/* Commission section */}
          {splitType === 'commission' && <div className="space-y-6" data-id="element-8479">
              {/* Commission configuration - moved from top card */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8480">
                <div data-id="element-8481">
                  <label className={labelClass} data-id="element-8482">
                    {t('components.InstallmentPayment.total_payment_amount')}{' '}
                    <span className="text-red-400" data-id="element-8483">*</span>
                  </label>
                  <div className="relative" data-id="element-8484">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8485">
                      <span className="text-sm font-medium text-gray-500" data-id="element-8486">
                        {t('components.BulkPaymentListSimplified.usd')}
                      </span>
                    </div>
                    <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={totalPaymentAmount} onChange={e => handleTotalPaymentAmountChange(e.target.value)} data-id="element-8487" />
                  </div>
                </div>
                <div data-id="element-8488">
                  <label className={labelClass} data-id="element-8489">
                    {t('components.InstallmentPayment.commission_type')}
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-8490">
                    <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${beneficiaryType === 'percentage' ? 'border-[var(--brand-primary)] bg-[var(--brand-primarysubtle)] ring-1 ring-[var(--brand-primary)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-8491">
                      <input type="radio" name="beneficiaryType" className="h-4 w-4 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] border-gray-300" checked={beneficiaryType === 'percentage'} onChange={() => setBeneficiaryType('percentage')} data-id="element-8492" />

                      <div className="flex items-center gap-2" data-id="element-8493">
                        <PercentIcon size={14} className={beneficiaryType === 'percentage' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-8494" />

                        <span className="text-sm font-medium text-gray-900" data-id="element-8495">
                          {t('components.InstallmentPayment.percentage')}
                        </span>
                      </div>
                    </label>

                    <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${beneficiaryType === 'fixed' ? 'border-[var(--brand-primary)] bg-[var(--brand-primarysubtle)] ring-1 ring-[var(--brand-primary)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-8496">
                      <input type="radio" name="beneficiaryType" className="h-4 w-4 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] border-gray-300" checked={beneficiaryType === 'fixed'} onChange={() => setBeneficiaryType('fixed')} data-id="element-8497" />

                      <div className="flex items-center gap-2" data-id="element-8498">
                        <DollarSignIcon size={14} className={beneficiaryType === 'fixed' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-8499" />

                        <span className="text-sm font-medium text-gray-900" data-id="element-8500">
                          {t('components.InstallmentPayment.fixed_amount')}
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {commissionError && <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-start border border-red-100" data-id="element-8501">
                  <AlertCircleIcon size={18} className="mr-3 mt-0.5 flex-shrink-0" data-id="element-8502" />

                  <span className="text-sm font-medium" data-id="element-8503">{commissionError}</span>
                </div>}

              {suppliers.map((supplier, index) => <div key={supplier.id} className="flex items-start space-x-4 p-5 bg-gray-50 rounded-xl border border-gray-100" data-id="element-8504">
                  <div className="w-8 h-8 flex items-center justify-center bg-[var(--brand-primarylight)] text-[var(--brand-primarytextonwhite)] rounded-full font-bold text-sm flex-shrink-0" data-id="element-8505">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-4" data-id="element-8506">
                    <div data-id="element-8507">
                      <label className={labelClass} data-id="element-8508">
                        {t('components.InstallmentPayment.select_supplier')}
                      </label>
                      <SearchableSelect options={supplierOptions} value={supplier.name} onChange={val => {
                  const selected = mockSuppliers.find(s => s.name === val);
                  // Update both name and email in a single state call to avoid stale state
                  setSuppliers(suppliers.map(s => s.id === supplier.id ? {
                    ...s,
                    name: selected ? selected.name : val,
                    email: selected ? selected.email : s.email
                  } : s));
                }} placeholder={t('components.InstallmentPayment.attrs.select_a_supplier')} data-id="element-8509" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8510">
                      <div data-id="element-8511">
                        <label className={labelClass} data-id="element-8512">
                          {beneficiaryType === 'percentage' ? 'Commission percentage' : 'Fixed amount'}
                        </label>
                        <div className="relative" data-id="element-8513">
                          {beneficiaryType === 'percentage' ? <>
                              <input type="number" className={inputClass} placeholder="0" min="0" max="100" value={supplier.percentage} onChange={e => updateSupplier(supplier.id, 'percentage', e.target.value)} data-id="element-8514" />

                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-8515">
                                <span className="text-sm font-medium text-gray-500" data-id="element-8516">
                                  %
                                </span>
                              </div>
                            </> : <>
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8517">
                                <span className="text-sm font-medium text-gray-500" data-id="element-8518">
                                  {t('components.BulkPaymentListSimplified.usd')}
                                </span>
                              </div>
                              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={supplier.amount} onChange={e => updateSupplier(supplier.id, 'amount', e.target.value)} data-id="element-8519" />
                            </>}
                        </div>
                        {beneficiaryType === 'percentage' && parseFloat(supplier.percentage || '0') > 0 && totalPaymentAmount && <div className="text-xs font-medium text-[#9F140F] mt-2" data-id="element-8520">
                              {formatCurrencyAmount(parseFloat(totalPaymentAmount) * parseFloat(supplier.percentage || '0') / 100, {
                      fractionDigits: 2
                    })}
                            </div>}
                      </div>
                      <div data-id="element-8521">
                        <label className={labelClass} data-id="element-8522">
                          {t('components.BulkPaymentPreview.description')}
                        </label>
                        <input type="text" className={inputClass} placeholder={t('components.InstallmentPayment.attrs.eg_commission_for_services')} value={supplier.description || ''} onChange={e => updateSupplier(supplier.id, 'description', e.target.value)} data-id="element-8523" />
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeSupplier(supplier.id)} className="text-gray-400 hover:text-red-500 mt-1 transition-colors" disabled={suppliers.length <= 1} data-id="element-8524">
                    <TrashIcon size={18} data-id="element-8525" />
                  </button>
                </div>)}

              <button type="button" onClick={addSupplier} className="mt-4 flex items-center text-sm font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-8526">
                <PlusIcon size={16} className="mr-1.5" data-id="element-8527" />
                {t('components.InstallmentPayment.add_another_supplier')}
              </button>
            </div>}

          {/* Dates and Recipients sections */}
          {(splitType === 'dates' || splitType === 'recipients') && <div className="space-y-4" data-id="element-8528">
              {installments.map((installment, index) => <div key={installment.id} className="flex items-start space-x-4 p-5 bg-gray-50 rounded-xl border border-gray-100" data-id="element-8529">
                  <div className="w-8 h-8 flex items-center justify-center bg-[var(--brand-primarylight)] text-[var(--brand-primarytextonwhite)] rounded-full font-bold text-sm flex-shrink-0" data-id="element-8530">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-4" data-id="element-8531">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-8532">
                      <div data-id="element-8533">
                        <label className={labelClass} data-id="element-8534">
                          {t('components.BulkPaymentPreview.amount')}
                        </label>
                        <div className="relative" data-id="element-8535">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8536">
                            <span className="text-sm font-medium text-gray-500" data-id="element-8537">
                              {t('components.BulkPaymentListSimplified.usd')}
                            </span>
                          </div>
                          <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={installment.amount} onChange={e => updateInstallment(installment.id, 'amount', e.target.value)} data-id="element-8538" />
                        </div>
                      </div>

                      {splitType === 'dates' ? <>
                          <div className="relative" data-id="element-8539">
                            <label className={labelClass} data-id="element-8540">
                              {t('components.InstallmentPayment.due_date')}
                            </label>
                            <div className="relative" data-id="element-8541">
                              <input type="text" className={`${inputClass} pl-10 cursor-pointer`} placeholder={t('components.CommonPaymentOptions.attrs.mmddyyyy')} value={installment.dueDate} onChange={e => updateInstallment(installment.id, 'dueDate', e.target.value)} onClick={() => toggleDatePicker(installment.id, 'due')} readOnly data-id="element-8542" />

                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8543">
                                <CalendarIcon size={16} className="text-gray-400" data-id="element-8544" />
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => toggleDatePicker(installment.id, 'due')} data-id="element-8545">
                                {showDatePickers[installment.id]?.due ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-8546" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-8547" />}
                              </div>
                            </div>

                            {/* Date Picker for Due Date */}
                            {showDatePickers[installment.id]?.due && <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-full min-w-[280px]" data-id="element-8548">
                                {(() => {
                        const calendar = generateCalendar(installment.dueDate);
                        return <>
                                      <div className="flex justify-between items-center mb-3" data-id="element-8549">
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handlePrevMonth(calendar, installment.id, 'due')} data-id="element-8550">
                                          <ChevronDownIcon size={16} className="transform rotate-90 text-gray-600" data-id="element-8551" />
                                        </button>
                                        <div className="font-semibold text-gray-900 text-sm" data-id="element-8552">
                                          {calendar.month.toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                              })}
                                        </div>
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handleNextMonth(calendar, installment.id, 'due')} data-id="element-8553">
                                          <ChevronDownIcon size={16} className="transform -rotate-90 text-gray-600" data-id="element-8554" />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium mb-2" data-id="element-8555">
                                        <div className="text-gray-400" data-id="element-8556">
                                          {t('components.FixedLinksPayment.su')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8557">
                                          {t('components.FixedLinksPayment.mo')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8558">
                                          {t('components.FixedLinksPayment.tu')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8559">
                                          {t('components.FixedLinksPayment.we')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8560">
                                          {t('components.FixedLinksPayment.th')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8561">
                                          {t('components.FixedLinksPayment.fr')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8562">
                                          {t('components.FixedLinksPayment.sa')}
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-8563">
                                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors mx-auto
                                                  ${isSelected(date, installment.dueDate) ? 'bg-[var(--brand-primary)] text-[#1A1A1A] shadow-sm' : ''}
                                                  ${isToday(date) && !isSelected(date, installment.dueDate) ? 'border border-[var(--brand-primary)] text-[var(--brand-primarytextonwhite)]' : ''}
                                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}
                                                `} onClick={() => handleDateSelect(installment.id, 'dueDate', date)} data-id="element-8564">
                                                {date.getDate()}
                                              </button> : <div key={index} className="w-8 h-8" data-id="element-8565"></div>))}
                                      </div>
                                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between" data-id="element-8566">
                                        <button type="button" onClick={() => handleDateSelect(installment.id, 'dueDate', new Date())} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-8567">
                                          Today
                                        </button>
                                        <button type="button" onClick={() => {
                              const tmrw = new Date();
                              tmrw.setDate(tmrw.getDate() + 1);
                              handleDateSelect(installment.id, 'dueDate', tmrw);
                            }} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-8568">
                                          Tomorrow
                                        </button>
                                        <button type="button" onClick={() => {
                              const nextWeek = new Date();
                              nextWeek.setDate(nextWeek.getDate() + 7);
                              handleDateSelect(installment.id, 'dueDate', nextWeek);
                            }} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-8569">
                                          Next Week
                                        </button>
                                      </div>
                                    </>;
                      })()}
                              </div>}
                          </div>

                          <div className="relative" data-id="element-8570">
                            <label className={labelClass} data-id="element-8571">
                              {t('components.InstallmentPayment.send_link_on')}
                            </label>
                            <div className="relative" data-id="element-8572">
                              <input type="text" className={`${inputClass} pl-10 cursor-pointer`} placeholder={t('components.CommonPaymentOptions.attrs.mmddyyyy')} value={installment.sendLinkDate} onChange={e => updateInstallment(installment.id, 'sendLinkDate', e.target.value)} onClick={() => toggleDatePicker(installment.id, 'send')} readOnly data-id="element-8573" />

                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8574">
                                <CalendarIcon size={16} className="text-gray-400" data-id="element-8575" />
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => toggleDatePicker(installment.id, 'send')} data-id="element-8576">
                                {showDatePickers[installment.id]?.send ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-8577" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-8578" />}
                              </div>
                            </div>

                            {/* Date Picker for Send Link Date */}
                            {showDatePickers[installment.id]?.send && <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-full min-w-[280px]" data-id="element-8579">
                                {(() => {
                        const calendar = generateCalendar(installment.sendLinkDate);
                        return <>
                                      <div className="flex justify-between items-center mb-3" data-id="element-8580">
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handlePrevMonth(calendar, installment.id, 'send')} data-id="element-8581">
                                          <ChevronDownIcon size={16} className="transform rotate-90 text-gray-600" data-id="element-8582" />
                                        </button>
                                        <div className="font-semibold text-gray-900 text-sm" data-id="element-8583">
                                          {calendar.month.toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                              })}
                                        </div>
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handleNextMonth(calendar, installment.id, 'send')} data-id="element-8584">
                                          <ChevronDownIcon size={16} className="transform -rotate-90 text-gray-600" data-id="element-8585" />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium mb-2" data-id="element-8586">
                                        <div className="text-gray-400" data-id="element-8587">
                                          {t('components.FixedLinksPayment.su')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8588">
                                          {t('components.FixedLinksPayment.mo')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8589">
                                          {t('components.FixedLinksPayment.tu')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8590">
                                          {t('components.FixedLinksPayment.we')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8591">
                                          {t('components.FixedLinksPayment.th')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8592">
                                          {t('components.FixedLinksPayment.fr')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-8593">
                                          {t('components.FixedLinksPayment.sa')}
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-8594">
                                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors mx-auto
                                                  ${isSelected(date, installment.sendLinkDate) ? 'bg-[var(--brand-primary)] text-[#1A1A1A] shadow-sm' : ''}
                                                  ${isToday(date) && !isSelected(date, installment.sendLinkDate) ? 'border border-[var(--brand-primary)] text-[var(--brand-primarytextonwhite)]' : ''}
                                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}
                                                `} onClick={() => handleDateSelect(installment.id, 'sendLinkDate', date)} data-id="element-8595">
                                                {date.getDate()}
                                              </button> : <div key={index} className="w-8 h-8" data-id="element-8596"></div>))}
                                      </div>
                                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between" data-id="element-8597">
                                        <button type="button" onClick={() => handleDateSelect(installment.id, 'sendLinkDate', new Date())} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-8598">
                                          Today
                                        </button>
                                        <button type="button" onClick={() => {
                              const tmrw = new Date();
                              tmrw.setDate(tmrw.getDate() + 1);
                              handleDateSelect(installment.id, 'sendLinkDate', tmrw);
                            }} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-8599">
                                          Tomorrow
                                        </button>
                                        <button type="button" onClick={() => {
                              const nextWeek = new Date();
                              nextWeek.setDate(nextWeek.getDate() + 7);
                              handleDateSelect(installment.id, 'sendLinkDate', nextWeek);
                            }} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-8600">
                                          Next Week
                                        </button>
                                      </div>
                                    </>;
                      })()}
                              </div>}
                          </div>
                        </> : <div className="col-span-2" data-id="element-8601">
                          <label className={labelClass} data-id="element-8602">
                            {t('components.InstallmentPayment.recipient_contact_information')}
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-8603">
                            <div className="relative" data-id="element-8604">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8605">
                                <MailIcon size={16} className="text-gray-400" data-id="element-8606" />
                              </div>
                              <input type="email" className={`${inputClass} pl-10`} placeholder={t('components.InstallmentPayment.attrs.email_address')} value={installment.recipient?.email || ''} onChange={e => updateRecipient(installment.id, 'email', e.target.value)} data-id="element-8607" />
                            </div>
                            <div className="relative" data-id="element-8608">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8609">
                                <SmartphoneIcon size={16} className="text-gray-400" data-id="element-8610" />
                              </div>
                              <input type="tel" className={`${inputClass} pl-10`} placeholder={t('components.InstallmentPayment.attrs.phone_number')} value={installment.recipient?.phone || ''} onChange={e => updateRecipient(installment.id, 'phone', e.target.value)} data-id="element-8611" />
                            </div>
                          </div>
                        </div>}
                    </div>

                    <div data-id="element-8612">
                      <label className={labelClass} data-id="element-8613">
                        {t('components.BulkPaymentPreview.description')}
                      </label>
                      <input type="text" className={inputClass} placeholder={splitType === 'dates' ? 'e.g., First installment' : "e.g., John's share"} value={installment.description} onChange={e => updateInstallment(installment.id, 'description', e.target.value)} data-id="element-8614" />
                    </div>
                  </div>

                  <button type="button" onClick={() => removeInstallment(installment.id)} className="text-gray-400 hover:text-red-500 mt-1 transition-colors" disabled={installments.length <= 1} data-id="element-8615">
                    <TrashIcon size={18} data-id="element-8616" />
                  </button>
                </div>)}

              <button type="button" onClick={addInstallment} className="mt-4 flex items-center text-sm font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-8617">
                <PlusIcon size={16} className="mr-1.5" data-id="element-8618" />
                {t('components.InstallmentPayment.add_another')}{' '}
                {splitType === 'dates' ? 'payment date' : 'recipient'}
              </button>
            </div>}
        </div>
      </div>

      {/* ─── FEE CONFIGURATION ─── */}
      {splitType === 'commission' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8619">
          <div className="px-6 py-4 border-b border-gray-100" data-id="element-8620">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-8621">
              {t('components.InstallmentPayment.fee_configuration')}
            </h4>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-8622">
              {t('components.InstallmentPayment.choose_who_pays_the_transaction_processing')}
            </p>
          </div>
          <div className="p-6 space-y-5" data-id="element-8623">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-8624">
              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'supplier' ? 'border-[var(--brand-primary)] bg-[var(--brand-primarysubtle)] ring-1 ring-[var(--brand-primary)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-8625">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] border-gray-300" checked={feeType === 'supplier'} onChange={() => {
              setFeeType('supplier');
              setPassFeesToCustomer(false);
            }} data-id="element-8626" />

                <div data-id="element-8627">
                  <div className="flex items-center gap-2 mb-1" data-id="element-8628">
                    <BuildingIcon size={16} className={feeType === 'supplier' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-8629" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-8630">
                      {t('components.InstallmentPayment.supplier_pays')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-8631">
                    {t('components.InstallmentPayment.fee_is_deducted_from_the_suppliers')}
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'merchant' ? 'border-[var(--brand-primary)] bg-[var(--brand-primarysubtle)] ring-1 ring-[var(--brand-primary)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-8632">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] border-gray-300" checked={feeType === 'merchant'} onChange={() => {
              setFeeType('merchant');
              setPassFeesToCustomer(false);
            }} data-id="element-8633" />

                <div data-id="element-8634">
                  <div className="flex items-center gap-2 mb-1" data-id="element-8635">
                    <UserIcon size={16} className={feeType === 'merchant' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-8636" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-8637">
                      {t('components.InstallmentPayment.my_business_pays')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-8638">
                    {t('components.InstallmentPayment.fee_is_deducted_from_your_share')}
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'split' ? 'border-[var(--brand-primary)] bg-[var(--brand-primarysubtle)] ring-1 ring-[var(--brand-primary)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-8639">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] border-gray-300" checked={feeType === 'split'} onChange={() => {
              setFeeType('split');
              setPassFeesToCustomer(false);
            }} data-id="element-8640" />

                <div data-id="element-8641">
                  <div className="flex items-center gap-2 mb-1" data-id="element-8642">
                    <PercentIcon size={16} className={feeType === 'split' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-8643" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-8644">
                      {t('components.InstallmentPayment.split_the_fee')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-8645">
                    {t('components.InstallmentPayment.share_the_fee_between_your_business')}
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'customer' ? 'border-[var(--brand-primary)] bg-[var(--brand-primarysubtle)] ring-1 ring-[var(--brand-primary)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-8646">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] border-gray-300" checked={feeType === 'customer'} onChange={() => {
              setFeeType('customer');
              setPassFeesToCustomer(true);
            }} data-id="element-8647" />

                <div data-id="element-8648">
                  <div className="flex items-center gap-2 mb-1" data-id="element-8649">
                    <UsersIcon size={16} className={feeType === 'customer' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-8650" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-8651">
                      {t('components.InstallmentPayment.customer_pays')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-8652">
                    {t('components.InstallmentPayment.fee_is_added_to_the_total')}
                  </p>
                </div>
              </label>
            </div>

            {feeType === 'split' && <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100" data-id="element-8653">
                <label className={labelClass} data-id="element-8654">
                  {t('components.InstallmentPayment.supplier_fee_percentage')}
                </label>
                <div className="relative w-full sm:w-1/3" data-id="element-8655">
                  <input type="number" className={inputClass} placeholder="50" min="0" max="100" defaultValue="50" data-id="element-8656" />

                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-8657">
                    <span className="text-sm font-medium text-gray-500" data-id="element-8658">%</span>
                  </div>
                </div>
              </div>}
          </div>
        </div>}

      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} passFeesToCustomer={passFeesToCustomer} onPassFeesToCustomerChange={val => {
      setPassFeesToCustomer(val);
      if (val) {
        setFeeType('customer');
      } else if (feeType === 'customer') {
        setFeeType('supplier');
      }
    }} data-id="element-8659" />
    </div>;
}